import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useWeb3React } from '@web3-react/core';
import { transactionService, RecentTransaction } from '../../services/transactionService';
import { getMultipleBalances } from '../../utils/balanceUtils';
import { errorLogger, ErrorCategory, ErrorSeverity } from '../../utils/errorLogger';
import {
  getTokensForNetwork,
  getTokenConfig,
  getTokenAddress,
  TokenConfig,
  getBSCTokenStatus
} from '../../config/tokens';
import {
  getNetworkConfig,
  getNetworkDisplayName,
  isSupportedNetwork
} from '../../config/networks';
import { fetchTokenPrices } from '../../services/binancePriceService';
import './SwapInterface.css';

// Import the background image
const backgroundImage = '/blocks.png';

interface Token {
  symbol: string;
  name: string;
  logo: string;
  address: string | null; // null for native tokens
  balance: string;
  price: number;
  change24h: number;
  decimals: number;
  isStablecoin: boolean;
  chainId: number;
}

interface SwapState {
  fromToken: Token | null;
  toToken: Token | null;
  fromAmount: string;
  toAmount: string;
  slippage: number;
  loading: boolean;
  priceImpact: number;
  minimumReceived: string;
  gasEstimate: string;
  error: string | null;
}

interface BalanceState {
  loading: boolean;
  error: string | null;
  retryCount: number;
  lastRetryTime: number | null;
}

const SwapInterface: React.FC = () => {
  const { provider, account, isActive, chainId } = useWeb3React();

  const [swapState, setSwapState] = useState<SwapState>({
    fromToken: null,
    toToken: null,
    fromAmount: '',
    toAmount: '',
    slippage: 0.5,
    loading: false,
    priceImpact: 0,
    minimumReceived: '',
    gasEstimate: '',
    error: null
  });

  const [isTokenSelectorOpen, setIsTokenSelectorOpen] = useState<'from' | 'to' | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isWalletConnected, setIsWalletConnected] = useState(false);
  const [balanceState, setBalanceState] = useState<BalanceState>({
    loading: false,
    error: null,
    retryCount: 0,
    lastRetryTime: null
  });
  const [showTransactionHistory, setShowTransactionHistory] = useState(true);
  const [currentTransactions, setCurrentTransactions] = useState<any[]>([]);
  const [availableTokens, setAvailableTokens] = useState<Token[]>([]);
  const [, setTokenBalances] = useState<{ [symbol: string]: string }>({});
  const [, setCurrentNetwork] = useState<{
    chainId: number;
    name: string;
    isSupported: boolean;
  } | null>(null);

  // Sync wallet connection state with useWeb3React
  useEffect(() => {
    setIsWalletConnected(isActive);
  }, [isActive]);

  // Network detection and token loading
  useEffect(() => {
    const updateNetworkAndTokens = async () => {
      // Use connected network or default to Ethereum mainnet
      const currentChainId = chainId || 1;
      
      try {
        // Update current network info
        getNetworkConfig(currentChainId);
        const isSupported = isSupportedNetwork(currentChainId);
        
        setCurrentNetwork({
          chainId: currentChainId,
          name: getNetworkDisplayName(currentChainId),
          isSupported
        });

        if (!isSupported) {
          console.warn(`Unsupported network detected: ${currentChainId}`);
          // Still load tokens for Ethereum as fallback
          const fallbackChainId = 1;
          const fallbackTokens = getTokensForNetwork(fallbackChainId);
          console.log(`Loading fallback tokens for Ethereum:`, fallbackTokens);
          
          if (fallbackTokens.length === 0) {
            console.warn('No fallback tokens found for Ethereum');
          }

          const tokensWithPrices = await createTokensWithPrices(fallbackTokens, fallbackChainId);
          setAvailableTokens(tokensWithPrices);
          return;
        }

        // Load tokens for current network
        const networkTokens = getTokensForNetwork(currentChainId);
        console.log(`Loading tokens for network ${currentChainId}:`, networkTokens);

        if (networkTokens.length === 0) {
          console.warn(`No tokens found for network ${currentChainId}`);
        }

        const tokensWithPrices = await createTokensWithPrices(networkTokens, currentChainId);
        setAvailableTokens(tokensWithPrices);
        
      } catch (error) {
        console.error('Failed to update network and tokens:', error);
        // Load fallback tokens for Ethereum
        try {
          const fallbackTokens = getTokensForNetwork(1);
          const tokensWithPrices = await createTokensWithPrices(fallbackTokens, 1);
          setAvailableTokens(tokensWithPrices);
          setCurrentNetwork({
            chainId: 1,
            name: 'Ethereum (Fallback)',
            isSupported: true
          });
        } catch (fallbackError) {
          console.error('Failed to load fallback tokens:', fallbackError);
          setCurrentNetwork(null);
          setAvailableTokens([]);
        }
      }
    };

    // Helper function to create tokens with price data
    const createTokensWithPrices = async (networkTokens: TokenConfig[], chainId: number): Promise<Token[]> => {
      // Collect all coingecko IDs that have valid IDs
      const validTokens = networkTokens.filter(token => token.coingeckoId && token.coingeckoId.trim() !== '');
      const coingeckoIds = validTokens.map(token => token.coingeckoId!);

      // Fetch real prices if we have valid IDs
      let priceData: any = {};
      if (coingeckoIds.length > 0) {
        try {
          priceData = await fetchTokenPrices(coingeckoIds);
          console.log('Fetched real prices:', priceData);
        } catch (error) {
          console.error('Failed to fetch real prices, using fallback:', error);
          priceData = {};
        }
      }

      return networkTokens.map((tokenConfig) => {
        const address = getTokenAddress(tokenConfig.symbol, chainId);

        // Try to get real price data first
        let price = 1.00;
        let change24h = 0.0;

        if (tokenConfig.coingeckoId && priceData[tokenConfig.coingeckoId]) {
          const realPriceData = priceData[tokenConfig.coingeckoId];
          price = realPriceData.usd || 1.00;
          change24h = realPriceData.usd_24h_change || 0.0;
        } else {
          // Fallback to mock data for tokens without coingecko ID or failed fetch
          switch (tokenConfig.symbol) {
            case 'ETH':
              price = 3847.32;
              change24h = 5.23;
              break;
            case 'BNB':
              price = 245.67;
              change24h = 1.45;
              break;
            case 'USDP':
              price = 1.00;
              change24h = 0.03;
              break;
            case 'USDT':
              price = 1.00;
              change24h = 0.03;
              break;
            case 'USDC':
              price = 1.00;
              change24h = -0.02;
              break;
          }
        }

        const config = getTokenConfig(tokenConfig.symbol);
        const token = {
          symbol: tokenConfig.symbol,
          name: tokenConfig.name,
          logo: config?.logoUrl || '/USDPlogo.png',
          address,
          balance: '0',
          price,
          change24h,
          decimals: tokenConfig.decimals,
          isStablecoin: tokenConfig.isStablecoin,
          chainId
        };
        return token;
      });
    };

    updateNetworkAndTokens();
  }, [chainId]); // Removed isActive dependency so tokens load even without wallet

  // Enhanced balance fetching with error handling and retry mechanism
  useEffect(() => {
    console.log('🔄 [DIAGNOSTIC] Balance useEffect triggered:', {
      timestamp: new Date().toISOString(),
      isActive,
      hasAccount: !!account,
      hasProvider: !!provider,
      tokenCount: availableTokens.length,
      retryCount: balanceState.retryCount,
      chainId,
      stackTrace: new Error().stack?.split('\n').slice(1, 3).join('\n')
    });

    const fetchBalances = async () => {
      if (!isActive || !account || !provider || availableTokens.length === 0) {
        console.log('🚫 [DIAGNOSTIC] Balance fetch skipped:', {
          isActive,
          hasAccount: !!account,
          hasProvider: !!provider,
          tokenCount: availableTokens.length
        });
        setBalanceState({ loading: false, error: null, retryCount: 0, lastRetryTime: null });
        setTokenBalances({});
        return;
      }

      console.log('🔍 [DIAGNOSTIC] Starting balance fetch for', availableTokens.length, 'tokens, retry count:', balanceState.retryCount);
      setBalanceState(prev => ({ ...prev, loading: true, error: null }));
      
      try {
        const tokenData = availableTokens.map(token => ({
          address: token.address,
          symbol: token.symbol
        }));
        
        console.log('Token data for balance fetch:', tokenData);
        
        const balances = await getMultipleBalances(provider as any, account, tokenData);
        console.log('Balance fetch results:', balances);
        
        // Check for any balance errors
        const hasErrors = balances.some(balance => balance.error);
        
        if (hasErrors) {
          const errorMessages = balances
            .filter(balance => balance.error)
            .map(balance => `${balance.symbol}: ${balance.error!.message}`)
            .join('; ');
          
          console.warn('Some balance fetches failed:', errorMessages);
          
          // Only set error state for critical errors (all balances failed)
          const allFailed = balances.every(balance => balance.error);
          if (allFailed) {
            const firstError = balances[0].error!;
            const errorMessage = firstError.retryable
              ? 'Failed to load balances. Click retry to try again.'
              : 'Unable to load token balances. Please check your network connection.';
              
            setBalanceState(prev => {
              // Log the critical balance error
              errorLogger.logError(
                ErrorCategory.BALANCE_FETCH,
                ErrorSeverity.HIGH,
                `All balance fetches failed: ${firstError.message}`,
                errorMessage,
                { tokenCount: tokenData.length, errorType: firstError.type },
                firstError.originalError,
                firstError.retryable,
                prev.retryCount + 1
              );
              
              return {
                ...prev,
                loading: false,
                error: errorMessage,
                retryCount: prev.retryCount + 1,
                lastRetryTime: Date.now()
              };
            });
            return;
          }
        }
        
        // Update token balances separately to avoid circular dependency
        const newBalances: { [symbol: string]: string } = {};
        balances.forEach(balanceResult => {
          const newBalance = balanceResult.error
            ? '0'
            : parseFloat(balanceResult.balance).toFixed(6);
          
          console.log(`Updating balance for ${balanceResult.symbol}: ${newBalance}`);
          newBalances[balanceResult.symbol] = newBalance;
          
          // Log placeholder warnings for visibility
          if ((balanceResult as any).placeholderWarning?.isPlaceholder) {
            console.warn(`🔄 ${(balanceResult as any).placeholderWarning.warningMessage}`);
          }
        });
        
        setTokenBalances(newBalances);
        setBalanceState({ loading: false, error: null, retryCount: 0, lastRetryTime: null });
        
        // Log BSC-specific token status when on BSC network
        if (chainId === 56) {
          const bscStatus = getBSCTokenStatus();
          console.log('🔍 BSC Token Status:', {
            supported: bscStatus.supportedTokens,
            placeholders: bscStatus.placeholderTokens.length,
            native: bscStatus.nativeTokens.length,
            valid: bscStatus.validTokens.length
          });
          
          if (bscStatus.placeholderTokens.length > 0) {
            console.warn('⚠️ BSC Placeholder Tokens Detected:');
            bscStatus.placeholderTokens.forEach(token => {
              console.warn(`  - ${token.symbol}: ${token.warningMessage}`);
            });
          }
        }
        
      } catch (error: any) {
        console.error('Critical balance fetch error:', error);
        setBalanceState(prev => {
          // Log critical balance fetch error
          errorLogger.logError(
            ErrorCategory.BALANCE_FETCH,
            ErrorSeverity.CRITICAL,
            `Critical balance fetch error: ${error?.message || 'Unknown error'}`,
            'Failed to load balances. Please check your connection and try again.',
            { account, tokenCount: availableTokens.length },
            error,
            true,
            prev.retryCount + 1
          );
          
          return {
            ...prev,
            loading: false,
            error: 'Failed to load balances. Please check your connection and try again.',
            retryCount: prev.retryCount + 1,
            lastRetryTime: Date.now()
          };
        });
      }
    };

    fetchBalances();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, account, provider, availableTokens, chainId]);
  // Removed balanceState.retryCount from dependencies to prevent infinite loop


  // Retry balance fetching - Fixed to avoid infinite loop
  const retryBalanceFetch = () => {
    console.log('Manual balance retry triggered');
    // Force re-fetch by clearing error state and triggering manual fetch
    setBalanceState(prev => ({ ...prev, error: null, loading: true }));
    
    // Manually trigger fetchBalances after state update
    setTimeout(() => {
      if (isActive && account && provider && availableTokens.length > 0) {
        // Re-run the fetch logic without relying on useEffect
        fetchBalancesManually();
      }
    }, 100);
  };

  // Manual balance fetch function to avoid useEffect dependency issues
  const fetchBalancesManually = async () => {
    if (!isActive || !account || !provider || availableTokens.length === 0) {
      console.log('🚫 [DIAGNOSTIC] Manual balance fetch skipped:', {
        isActive,
        hasAccount: !!account,
        hasProvider: !!provider,
        tokenCount: availableTokens.length
      });
      setBalanceState({ loading: false, error: null, retryCount: 0, lastRetryTime: null });
      setTokenBalances({});
      return;
    }

    console.log('🔍 [DIAGNOSTIC] Manual balance fetch starting for', availableTokens.length, 'tokens');
    setBalanceState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const tokenData = availableTokens.map(token => ({
        address: token.address,
        symbol: token.symbol
      }));
      
      console.log('Token data for manual balance fetch:', tokenData);
      
      const balances = await getMultipleBalances(provider as any, account, tokenData);
      console.log('Manual balance fetch results:', balances);
      
      // Check for any balance errors
      const hasErrors = balances.some(balance => balance.error);
      
      if (hasErrors) {
        const errorMessages = balances
          .filter(balance => balance.error)
          .map(balance => `${balance.symbol}: ${balance.error!.message}`)
          .join('; ');
        
        console.warn('Some manual balance fetches failed:', errorMessages);
        
        // Only set error state for critical errors (all balances failed)
        const allFailed = balances.every(balance => balance.error);
        if (allFailed) {
          const firstError = balances[0].error!;
          const errorMessage = firstError.retryable
            ? 'Failed to load balances. Click retry to try again.'
            : 'Unable to load token balances. Please check your network connection.';
            
          setBalanceState(prev => ({
            ...prev,
            loading: false,
            error: errorMessage,
            retryCount: prev.retryCount + 1,
            lastRetryTime: Date.now()
          }));
          return;
        }
      }
      
      // Update token balances
      const newBalances: { [symbol: string]: string } = {};
      balances.forEach(balanceResult => {
        const newBalance = balanceResult.error
          ? '0'
          : parseFloat(balanceResult.balance).toFixed(6);
        
        console.log(`Updating manual balance for ${balanceResult.symbol}: ${newBalance}`);
        newBalances[balanceResult.symbol] = newBalance;
      });
      
      setTokenBalances(newBalances);
      setBalanceState({ loading: false, error: null, retryCount: 0, lastRetryTime: null });
      
    } catch (error: any) {
      console.error('Critical manual balance fetch error:', error);
      setBalanceState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load balances. Please check your connection and try again.',
        retryCount: prev.retryCount + 1,
        lastRetryTime: Date.now()
      }));
    }
  };

  // Dynamic transaction generation system - reserved for future use
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _generateRandomTransaction = () => {
    const tokens = ['USDP', 'ETH', 'USDC', 'USDT', 'WBTC', 'BNB', 'LINK', 'UNI', 'AAVE'];
    const timeOptions = ['2m ago', '5m ago', '10m ago', '15m ago', '30m ago', '1h ago', '2h ago', '3h ago'];
    
    const fromToken = tokens[Math.floor(Math.random() * tokens.length)];
    let toToken = tokens[Math.floor(Math.random() * tokens.length)];
    while (toToken === fromToken) {
      toToken = tokens[Math.floor(Math.random() * tokens.length)];
    }
    
    // Get token data for price calculations
    const fromTokenData = availableTokens.find(t => t.symbol === fromToken);
    const toTokenData = availableTokens.find(t => t.symbol === toToken);
    
    if (!fromTokenData || !toTokenData) return null;
    
    // Generate realistic amounts
    let fromAmount, toAmount;
    if (fromToken === 'USDP' || fromToken === 'USDC' || fromToken === 'USDT') {
      fromAmount = Math.floor(Math.random() * 50000) + 1000;
    } else if (fromToken === 'ETH') {
      fromAmount = (Math.random() * 50 + 0.1).toFixed(1);
    } else if (fromToken === 'WBTC') {
      fromAmount = (Math.random() * 5 + 0.01).toFixed(2);
    } else {
      fromAmount = Math.floor(Math.random() * 1000) + 10;
    }
    
    // Calculate conversion
    const fromValue = parseFloat(fromAmount.toString()) * fromTokenData.price;
    toAmount = (fromValue / toTokenData.price).toFixed(toToken === 'WBTC' ? 3 : 0);
    
    return {
      type: 'Swap',
      from: fromToken,
      to: toToken,
      fromAmount: fromAmount,
      toAmount: toAmount,
      amount: `${fromAmount} ${fromToken} → ${toAmount} ${toToken}`,
      time: timeOptions[Math.floor(Math.random() * timeOptions.length)],
      status: Math.random() > 0.1 ? 'confirmed' : 'pending',
      fromLogo: fromTokenData.logo,
      toLogo: toTokenData.logo
    };
  };

  // Initialize with default transactions - reserved for future use
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _initialTransactions = [
    {
      type: 'Swap',
      from: 'ETH',
      to: 'USDP',
      fromAmount: '10',
      toAmount: '3,847',
      amount: '10 ETH → 3,847 USDP',
      time: '2m ago',
      status: 'confirmed',
      fromLogo: '/icons/ETH.svg',
      toLogo: '/USDPlogo.png'
    },
    {
      type: 'Swap',
      from: 'USDC',
      to: 'USDP',
      fromAmount: '1,000',
      toAmount: '1,000',
      amount: '1,000 USDC → 1,000 USDP',
      time: '15m ago',
      status: 'confirmed',
      fromLogo: '/icons/usdp.svg',
      toLogo: '/USDPlogo.png'
    },
    {
      type: 'Swap',
      from: 'USDP',
      to: 'WBTC',
      fromAmount: '50,000',
      toAmount: '0.74',
      amount: '50,000 USDP → 0.74 WBTC',
      time: '1h ago',
      status: 'confirmed',
      fromLogo: '/USDPlogo.png',
      toLogo: '/icons/wbtc.svg'
    }
  ];

  // Load recent transactions and subscribe to real-time updates - FIXED: Remove initialTransactions dependency
  React.useEffect(() => {
    console.log('🔄 [DIAGNOSTIC] Transaction subscription useEffect triggered:', {
      timestamp: new Date().toISOString(),
      chainId,
      stackTrace: new Error().stack?.split('\n').slice(1, 3).join('\n')
    });

    const loadRecentTransactions = async () => {
      try {
        // Load from centralized transaction service
        const recentTxs = await transactionService.getRecentTransactions(8);
        // Only show real transactions; do not use placeholder fallbacks
        setCurrentTransactions(recentTxs);
      } catch (error) {
        console.error('Failed to load transactions:', error);
        // On error, show no transactions instead of placeholders
        setCurrentTransactions([]);
      }
    };

    // Load initial transactions
    loadRecentTransactions();
    
    // Subscribe to real-time transaction updates
    const unsubscribe = transactionService.subscribe((newTransaction: RecentTransaction) => {
      console.log('📡 [DIAGNOSTIC] SwapInterface received new transaction:', newTransaction);
      setCurrentTransactions(prev => [newTransaction, ...prev.slice(0, 7)]);
    });
    
    // DON'T auto-start transaction service to reduce resource usage
    // const status = transactionService.getStatus();
    // if (!status.isGenerating) {
    //   console.log('🔄 Starting transaction generation from SwapInterface');
    //   transactionService.startGeneration();
    // }
    
    // Update configuration for current network
    if (chainId) {
      transactionService.updateConfig({ networkId: chainId });
    }
    
    return () => {
      unsubscribe();
    };
  }, [chainId]); // FIXED: Removed initialTransactions dependency to prevent subscription leaks

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _formatTimeAgo = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const recentTransactions = currentTransactions;

  const filteredTokens = availableTokens.filter(token =>
    token.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
    token.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    console.log('🔄 [DIAGNOSTIC] Calculate swap useEffect triggered:', {
      timestamp: new Date().toISOString(),
      hasFromToken: !!swapState.fromToken,
      hasToToken: !!swapState.toToken,
      fromAmount: swapState.fromAmount,
      slippage: swapState.slippage,
      stackTrace: new Error().stack?.split('\n').slice(1, 3).join('\n')
    });

    if (swapState.fromToken && swapState.fromAmount && !isNaN(parseFloat(swapState.fromAmount))) {
      calculateSwap();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swapState.fromToken, swapState.toToken, swapState.fromAmount, swapState.slippage]);

  const calculateSwap = () => {
    console.log('⚡ [DIAGNOSTIC] calculateSwap called:', {
      timestamp: new Date().toISOString(),
      fromToken: swapState.fromToken?.symbol,
      toToken: swapState.toToken?.symbol,
      fromAmount: swapState.fromAmount,
      currentLoading: swapState.loading
    });

    if (!swapState.fromToken || !swapState.toToken || !swapState.fromAmount) return;

    setSwapState(prev => ({ ...prev, loading: true }));

    // Simulate API call delay
    setTimeout(() => {
      const fromAmount = parseFloat(swapState.fromAmount);
      const fromValue = fromAmount * swapState.fromToken!.price;
      const toAmount = fromValue / swapState.toToken!.price;
      
      // Calculate price impact (mock calculation)
      const priceImpact = Math.min(fromValue / 1000000 * 0.1, 5); // Max 5% impact
      
      // Calculate minimum received with slippage
      const minimumReceived = (toAmount * (1 - swapState.slippage / 100)).toFixed(6);
      
      // Mock gas estimate
      const gasEstimate = (0.002 + Math.random() * 0.003).toFixed(5);

      setSwapState(prev => ({
        ...prev,
        toAmount: toAmount.toFixed(6),
        priceImpact,
        minimumReceived,
        gasEstimate,
        loading: false,
        error: priceImpact > 3 ? 'High price impact warning' : null
      }));
    }, 500);
  };

  const handleTokenSelect = (token: Token) => {
    if (isTokenSelectorOpen === 'from') {
      setSwapState(prev => ({ ...prev, fromToken: token }));
    } else if (isTokenSelectorOpen === 'to') {
      setSwapState(prev => ({ ...prev, toToken: token }));
    }
    setIsTokenSelectorOpen(null);
    setSearchTerm('');
  };

  const handleSwapDirection = () => {
    setSwapState(prev => ({
      ...prev,
      fromToken: prev.toToken,
      toToken: prev.fromToken,
      fromAmount: prev.toAmount,
      toAmount: prev.fromAmount
    }));
  };

  const handleMaxAmount = () => {
    if (swapState.fromToken) {
      setSwapState(prev => ({ ...prev, fromAmount: swapState.fromToken!.balance.replace(',', '') }));
    }
  };

  const handleSwap = async () => {
    if (!isWalletConnected) {
      setSwapState(prev => ({ ...prev, error: 'Please connect your wallet first' }));
      return;
    }

    if (!swapState.fromToken || !swapState.toToken || !swapState.fromAmount) {
      setSwapState(prev => ({ ...prev, error: 'Please select tokens and enter an amount' }));
      return;
    }

    // Check if user has sufficient balance
    const fromBalance = parseFloat(swapState.fromToken.balance.replace(',', ''));
    const fromAmount = parseFloat(swapState.fromAmount);
    
    if (fromAmount > fromBalance) {
      setSwapState(prev => ({
        ...prev,
        error: `Insufficient ${swapState.fromToken!.symbol} balance. Available: ${fromBalance}`
      }));
      return;
    }
    
    setSwapState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      // Simulate swap transaction with error handling
      await new Promise((resolve, reject) => {
        setTimeout(() => {
          // Simulate 10% chance of swap failure
          if (Math.random() < 0.1) {
            reject(new Error('Transaction failed due to slippage'));
          } else {
            resolve(true);
          }
        }, 3000);
      });
      
      setSwapState(prev => ({ ...prev, loading: false, error: null }));
      
      // Show success state briefly
      setSwapState(prev => ({
        ...prev,
        error: 'Swap completed successfully!',
        fromAmount: '',
        toAmount: ''
      }));
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSwapState(prev => ({ ...prev, error: null }));
      }, 3000);
      
    } catch (error: any) {
      console.error('Swap transaction failed:', error);
      setSwapState(prev => ({
        ...prev,
        loading: false,
        error: error.message || 'Transaction failed. Please try again.'
      }));
    }
  };

  return (
    <section id="swap" className="swap-interface section-padding">
      <div className="swap-background">
        <div
          className="swap-image"
          style={{
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        ></div>
        <div className="swap-overlay"></div>
      </div>
      <div className="container">
        <motion.div
          className="swap-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">
            Swap <span className="title-gradient">USDP</span>
          </h2>
          <p className="section-subtitle">
            Trade USDP seamlessly against other cryptocurrencies with minimal slippage and competitive rates.
          </p>
        </motion.div>

        <div className="swap-container">
          <motion.div
            className="swap-widget"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="swap-header-controls">
              <div className="swap-tabs">
                <button className="tab-button active">Swap</button>
                <button className="tab-button">Limit</button>
              </div>
              <div className="swap-settings">
                <button 
                  className="settings-button"
                  onClick={() => setIsSettingsOpen(!isSettingsOpen)}
                >
                  ⚙️
                </button>
                {isSettingsOpen && (
                  <motion.div
                    className="settings-dropdown"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                  >
                    <div className="setting-item">
                      <label>Slippage Tolerance</label>
                      <div className="slippage-buttons">
                        {[0.1, 0.5, 1.0].map(value => (
                          <button
                            key={value}
                            className={`slippage-btn ${swapState.slippage === value ? 'active' : ''}`}
                            onClick={() => setSwapState(prev => ({ ...prev, slippage: value }))}
                          >
                            {value}%
                          </button>
                        ))}
                        <input
                          type="number"
                          className="custom-slippage"
                          placeholder="Custom"
                          value={swapState.slippage}
                          onChange={(e) => setSwapState(prev => ({ ...prev, slippage: parseFloat(e.target.value) || 0.5 }))}
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>

            <div className="swap-form">
              {/* From Token Section */}
              <div className="token-input-section">
                <div className="token-input-header">
                  <span className="input-label">You pay</span>
                  <span className="balance">
                    Balance: {balanceState.loading ? (
                      <span className="balance-loading">Loading...</span>
                    ) : balanceState.error ? (
                      <span className="balance-error-text">Error</span>
                    ) : (
                      swapState.fromToken?.balance || '0.00'
                    )}
                    {!balanceState.loading && !balanceState.error && (
                      <button className="max-button" onClick={handleMaxAmount}>MAX</button>
                    )}
                  </span>
                </div>
                <div className="token-input-container">
                  <button
                    className="token-selector"
                    onClick={() => setIsTokenSelectorOpen('from')}
                  >
                    {swapState.fromToken ? (
                      <>
                        <img
                          src={swapState.fromToken?.logo}
                          alt={swapState.fromToken?.symbol}
                          className="token-logo"
                          onLoad={() => console.log(`✅ Icon loaded successfully: ${swapState.fromToken?.logo}`)}
                          onError={(e) => {
                            console.warn(`❌ Icon failed to load: ${swapState.fromToken?.logo}, falling back to /USDPlogo.png`);
                            (e.target as HTMLImageElement).src = '/USDPlogo.png';
                          }}
                        />
                        <span className="token-symbol">{swapState.fromToken.symbol === 'USDP' ? 'USD.P' : swapState.fromToken.symbol}</span>
                      </>
                    ) : (
                      <span className="select-token">Select token</span>
                    )}
                  </button>
                  <input
                    type="number"
                    className="amount-input"
                    placeholder={!isWalletConnected ? "Connect wallet first" : "0.0"}
                    value={swapState.fromAmount}
                    onChange={(e) => setSwapState(prev => ({ ...prev, fromAmount: e.target.value }))}
                    disabled={!isWalletConnected || balanceState.loading}
                  />
                </div>
                {swapState.fromToken && (
                  <div className="token-value">
                    ≈ ${(parseFloat(swapState.fromAmount) * swapState.fromToken.price || 0).toFixed(2)}
                  </div>
                )}
              </div>

              {/* Swap Direction Arrow */}
              <div className="swap-direction">
                <motion.button
                  className="swap-arrow-button"
                  onClick={handleSwapDirection}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  ⇅
                </motion.button>
              </div>

              {/* To Token Section */}
              <div className="token-input-section">
                <div className="token-input-header">
                  <span className="input-label">You receive</span>
                  <span className="balance">
                    Balance: {balanceState.loading ? (
                      <span className="balance-loading">Loading...</span>
                    ) : balanceState.error ? (
                      <span className="balance-error-text">Error</span>
                    ) : (
                      swapState.toToken?.balance || '0.00'
                    )}
                  </span>
                </div>
                <div className="token-input-container">
                  <button
                    className="token-selector"
                    onClick={() => setIsTokenSelectorOpen('to')}
                  >
                    {swapState.toToken ? (
                      <>
                        <img
                          src={swapState.toToken?.logo}
                          alt={swapState.toToken?.symbol}
                          className="token-logo"
                          onLoad={() => console.log(`✅ Icon loaded successfully: ${swapState.toToken?.logo}`)}
                          onError={(e) => {
                            console.warn(`❌ Icon failed to load: ${swapState.toToken?.logo}, falling back to /USDPlogo.png`);
                            (e.target as HTMLImageElement).src = '/USDPlogo.png';
                          }}
                        />
                        <span className="token-symbol">{swapState.toToken.symbol === 'USDP' ? 'USD.P' : swapState.toToken.symbol}</span>
                      </>
                    ) : (
                      <span className="select-token">Select token</span>
                    )}
                    <span className="dropdown-arrow"></span>
                  </button>
                  <input
                    type="number"
                    className="amount-input"
                    placeholder={!isWalletConnected ? "Connect wallet first" : "0.0"}
                    value={swapState.toAmount}
                    readOnly
                  />
                </div>
                {swapState.toToken && swapState.toAmount && (
                  <div className="token-value">
                    ≈ ${(parseFloat(swapState.toAmount) * swapState.toToken.price || 0).toFixed(2)}
                  </div>
                )}
              </div>

              {/* Swap Details */}
              {swapState.fromToken && swapState.toToken && swapState.toAmount && (
                <motion.div
                  className="swap-details"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="detail-row">
                    <span>Price Impact</span>
                    <span className={`price-impact ${swapState.priceImpact > 3 ? 'high' : swapState.priceImpact > 1 ? 'medium' : 'low'}`}>
                      {swapState.priceImpact.toFixed(2)}%
                    </span>
                  </div>
                  <div className="detail-row">
                    <span>Minimum Received</span>
                    <span>{swapState.minimumReceived} {swapState.toToken.symbol === 'USDP' ? 'USD.P' : swapState.toToken.symbol}</span>
                  </div>
                  <div className="detail-row">
                    <span>Network Fee</span>
                    <span>{swapState.gasEstimate} ETH</span>
                  </div>
                  <div className="detail-row">
                    <span>Route</span>
                    <span>{swapState.fromToken.symbol === 'USDP' ? 'USD.P' : swapState.fromToken.symbol} → {swapState.toToken.symbol === 'USDP' ? 'USD.P' : swapState.toToken.symbol}</span>
                  </div>
                </motion.div>
              )}

              {/* Balance Error Display */}
              {balanceState.error && (
                <motion.div
                  className="balance-error-container"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="balance-error">
                    <div className="error-content">
                      <span className="error-icon">⚠️</span>
                      <span className="error-text">{balanceState.error}</span>
                    </div>
                    <button
                      className="retry-button"
                      onClick={retryBalanceFetch}
                      disabled={balanceState.loading}
                    >
                      {balanceState.loading ? '🔄' : 'Retry'}
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Swap Error Display */}
              {swapState.error && !swapState.error.includes('successfully') && (
                <div className="error-message">
                  ❌ {swapState.error}
                </div>
              )}

              {/* Success Display */}
              {swapState.error && swapState.error.includes('successfully') && (
                <div className="success-message">
                  ✅ {swapState.error}
                </div>
              )}

              {/* Swap Button */}
              <motion.button
                className={`swap-button ${!isWalletConnected ? 'connect-wallet' : ''} ${swapState.loading ? 'loading' : ''}`}
                onClick={isWalletConnected ? handleSwap : () => setIsWalletConnected(true)}
                disabled={swapState.loading || (!swapState.fromToken || !swapState.toToken || !swapState.fromAmount)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {swapState.loading ? (
                  <div className="loading-spinner">Loading...</div>
                ) : !isWalletConnected ? (
                  'Connect Wallet'
                ) : (
                  'Swap'
                )}
              </motion.button>
            </div>
          </motion.div>

          {/* Transaction History */}
          <motion.div
            className="transaction-history"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="history-header">
              <h3>Recent Swaps</h3>
              <button 
                className="view-all-button"
                onClick={() => setShowTransactionHistory(!showTransactionHistory)}
              >
                {showTransactionHistory ? 'Hide' : 'View All'}
              </button>
            </div>
            <div className="history-list">
              {recentTransactions.slice(0, showTransactionHistory ? recentTransactions.length : 3).map((tx, index) => (
                <motion.div
                  key={index}
                  className="history-item"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="tx-tokens">
                    <div className="token-pair">
                      <img
                        src={tx.fromLogo}
                        alt={tx.from}
                        className="token-icon"
                        onLoad={() => console.log(`✅ Transaction icon loaded: ${tx.fromLogo}`)}
                        onError={(e) => {
                          console.warn(`❌ Transaction icon failed: ${tx.fromLogo}, falling back to /USDPlogo.png`);
                          (e.target as HTMLImageElement).src = '/USDPlogo.png';
                        }}
                      />
                      <img
                        src={tx.toLogo}
                        alt={tx.to}
                        className="token-icon token-icon-overlap"
                        onLoad={() => console.log(`✅ Transaction icon loaded: ${tx.toLogo}`)}
                        onError={(e) => {
                          console.warn(`❌ Transaction icon failed: ${tx.toLogo}, falling back to /USDPlogo.png`);
                          (e.target as HTMLImageElement).src = '/USDPlogo.png';
                        }}
                      />
                    </div>
                  </div>
                  <div className="tx-details">
                    <div className="tx-amount">{tx.amount}</div>
                    <div className="tx-time">{tx.time}</div>
                  </div>
                  <div className={`tx-status ${tx.status}`}>
                    {tx.status === 'confirmed' ? '✅' : '⏳'}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Token Selection Modal */}
        <AnimatePresence>
          {isTokenSelectorOpen && (
            <motion.div
              className="token-selector-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsTokenSelectorOpen(null)}
            >
              <motion.div
                className="token-selector-modal"
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-header">
                  <h3>Select a token</h3>
                  <button 
                    className="close-button"
                    onClick={() => setIsTokenSelectorOpen(null)}
                  >
                    ✕
                  </button>
                </div>
                <div className="search-container">
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search name or paste address"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="token-list">
                  {filteredTokens.length > 0 ? (
                    filteredTokens.map((token) => (
                      <motion.button
                        key={`${token.symbol}-${token.chainId}`}
                        className="token-item"
                        onClick={() => handleTokenSelect(token)}
                        whileHover={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
                      >
                        <img
                          src={token.logo}
                          alt={token.symbol}
                          className="token-logo"
                          onLoad={() => console.log(`✅ Token selector icon loaded: ${token.logo}`)}
                          onError={(e) => {
                            console.warn(`❌ Token selector icon failed: ${token.logo}, falling back to /USDPlogo.png`);
                            (e.target as HTMLImageElement).src = '/USDPlogo.png';
                          }}
                        />
                        <div className="token-info">
                          <div className="token-symbol">{token.symbol === 'USDP' ? 'USD.P' : token.symbol}</div>
                          <div className="token-name">{token.symbol === 'USDP' ? 'USD.P E-Pay Dollar' : token.name}</div>
                        </div>
                        <div className="token-metrics">
                          <div className="token-price-row">
                            <span className="token-price">
                              ${token.price < 1
                                ? token.price.toPrecision(4)
                                : token.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                          <div className="token-details-row">
                            <span className={`price-change ${token.change24h >= 0 ? 'positive' : 'negative'}`}>
                              {token.change24h >= 0 ? '+' : ''}{token.change24h.toFixed(2)}%
                            </span>
                            {isWalletConnected && (
                              <span className={`balance-amount ${balanceState.loading ? 'loading' : balanceState.error ? 'error' : ''}`}>
                                {balanceState.loading ? '...' :
                                 balanceState.error ? 'Err' :
                                 `Bal: ${parseFloat(token.balance).toLocaleString('en-US', { maximumFractionDigits: 4 })}`}
                              </span>
                            )}
                          </div>
                        </div>
                      </motion.button>
                    ))
                  ) : (
                    <div className="no-tokens-message">
                      <div className="no-tokens-icon">🔍</div>
                      <div className="no-tokens-text">
                        {searchTerm ? 'No tokens found matching your search' : 'No tokens available'}
                      </div>
                      <div className="no-tokens-subtitle">
                        {searchTerm ? 'Try a different search term' : 'Check your network connection or try a different network'}
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default SwapInterface;